
/**
 * @license undefined
  * Copyright Google Inc. All Rights Reserved.
  * *
  * Use of this source code is governed by an MIT-style license that can be
  * found in the LICENSE file at https://angular.io/license
 * @abstract
 */
export abstract class AnimationAst {
public startTime: number = 0;
public playTime: number = 0;
/**
 * @abstract
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
visit(visitor: AnimationAstVisitor, context: any) {}
}

function AnimationAst_tsickle_Closure_declarations() {
 /** @type {?} */
AnimationAst.prototype.startTime;
 /** @type {?} */
AnimationAst.prototype.playTime;
}

/**
 * @abstract
 */
export abstract class AnimationStateAst extends AnimationAst {
/**
 * @abstract
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
visit(visitor: AnimationAstVisitor, context: any) {}
}

export interface AnimationAstVisitor {
  visitAnimationEntry(ast: AnimationEntryAst, context: any): any;
  visitAnimationStateDeclaration(ast: AnimationStateDeclarationAst, context: any): any;
  visitAnimationStateTransition(ast: AnimationStateTransitionAst, context: any): any;
  visitAnimationStep(ast: AnimationStepAst, context: any): any;
  visitAnimationSequence(ast: AnimationSequenceAst, context: any): any;
  visitAnimationGroup(ast: AnimationGroupAst, context: any): any;
  visitAnimationKeyframe(ast: AnimationKeyframeAst, context: any): any;
  visitAnimationStyles(ast: AnimationStylesAst, context: any): any;
}

export class AnimationEntryAst extends AnimationAst {
/**
 * @param {?} name
 * @param {?} stateDeclarations
 * @param {?} stateTransitions
 */
constructor(
public name: string,
public stateDeclarations: AnimationStateDeclarationAst[],
public stateTransitions: AnimationStateTransitionAst[]) {
    super();
  }
/**
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
visit(visitor: AnimationAstVisitor, context: any): any {
    return visitor.visitAnimationEntry(this, context);
  }
}

function AnimationEntryAst_tsickle_Closure_declarations() {
 /** @type {?} */
AnimationEntryAst.prototype.name;
 /** @type {?} */
AnimationEntryAst.prototype.stateDeclarations;
 /** @type {?} */
AnimationEntryAst.prototype.stateTransitions;
}


export class AnimationStateDeclarationAst extends AnimationStateAst {
/**
 * @param {?} stateName
 * @param {?} styles
 */
constructor(public stateName: string,
public styles: AnimationStylesAst) { super(); }
/**
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
visit(visitor: AnimationAstVisitor, context: any): any {
    return visitor.visitAnimationStateDeclaration(this, context);
  }
}

function AnimationStateDeclarationAst_tsickle_Closure_declarations() {
 /** @type {?} */
AnimationStateDeclarationAst.prototype.stateName;
 /** @type {?} */
AnimationStateDeclarationAst.prototype.styles;
}


export class AnimationStateTransitionExpression {
/**
 * @param {?} fromState
 * @param {?} toState
 */
constructor(public fromState: string,
public toState: string) {}
}

function AnimationStateTransitionExpression_tsickle_Closure_declarations() {
 /** @type {?} */
AnimationStateTransitionExpression.prototype.fromState;
 /** @type {?} */
AnimationStateTransitionExpression.prototype.toState;
}


export class AnimationStateTransitionAst extends AnimationStateAst {
/**
 * @param {?} stateChanges
 * @param {?} animation
 */
constructor(
public stateChanges: AnimationStateTransitionExpression[],
public animation: AnimationWithStepsAst) {
    super();
  }
/**
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
visit(visitor: AnimationAstVisitor, context: any): any {
    return visitor.visitAnimationStateTransition(this, context);
  }
}

function AnimationStateTransitionAst_tsickle_Closure_declarations() {
 /** @type {?} */
AnimationStateTransitionAst.prototype.stateChanges;
 /** @type {?} */
AnimationStateTransitionAst.prototype.animation;
}


export class AnimationStepAst extends AnimationAst {
/**
 * @param {?} startingStyles
 * @param {?} keyframes
 * @param {?} duration
 * @param {?} delay
 * @param {?} easing
 */
constructor(
public startingStyles: AnimationStylesAst,
public keyframes: AnimationKeyframeAst[],
public duration: number,
public delay: number,
public easing: string) {
    super();
  }
/**
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
visit(visitor: AnimationAstVisitor, context: any): any {
    return visitor.visitAnimationStep(this, context);
  }
}

function AnimationStepAst_tsickle_Closure_declarations() {
 /** @type {?} */
AnimationStepAst.prototype.startingStyles;
 /** @type {?} */
AnimationStepAst.prototype.keyframes;
 /** @type {?} */
AnimationStepAst.prototype.duration;
 /** @type {?} */
AnimationStepAst.prototype.delay;
 /** @type {?} */
AnimationStepAst.prototype.easing;
}


export class AnimationStylesAst extends AnimationAst {
/**
 * @param {?} styles
 */
constructor(public styles: Array<{[key: string]: string | number}>) { super(); }
/**
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
visit(visitor: AnimationAstVisitor, context: any): any {
    return visitor.visitAnimationStyles(this, context);
  }
}

function AnimationStylesAst_tsickle_Closure_declarations() {
 /** @type {?} */
AnimationStylesAst.prototype.styles;
}


export class AnimationKeyframeAst extends AnimationAst {
/**
 * @param {?} offset
 * @param {?} styles
 */
constructor(public offset: number,
public styles: AnimationStylesAst) { super(); }
/**
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
visit(visitor: AnimationAstVisitor, context: any): any {
    return visitor.visitAnimationKeyframe(this, context);
  }
}

function AnimationKeyframeAst_tsickle_Closure_declarations() {
 /** @type {?} */
AnimationKeyframeAst.prototype.offset;
 /** @type {?} */
AnimationKeyframeAst.prototype.styles;
}

/**
 * @abstract
 */
export abstract class AnimationWithStepsAst extends AnimationAst {
/**
 * @param {?} steps
 */
constructor(public steps: AnimationAst[]) { super(); }
}

function AnimationWithStepsAst_tsickle_Closure_declarations() {
 /** @type {?} */
AnimationWithStepsAst.prototype.steps;
}


export class AnimationGroupAst extends AnimationWithStepsAst {
/**
 * @param {?} steps
 */
constructor(steps: AnimationAst[]) { super(steps); }
/**
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
visit(visitor: AnimationAstVisitor, context: any): any {
    return visitor.visitAnimationGroup(this, context);
  }
}

export class AnimationSequenceAst extends AnimationWithStepsAst {
/**
 * @param {?} steps
 */
constructor(steps: AnimationAst[]) { super(steps); }
/**
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
visit(visitor: AnimationAstVisitor, context: any): any {
    return visitor.visitAnimationSequence(this, context);
  }
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
